import { useEffect, useMemo, useState } from 'react'
import type { InventoryItem, InventoryTransaction, OutstandingRow } from '@shared/types'
import { get, isOffline } from '../lib/api'
import { enqueue, newClientId } from '../lib/outbox'
import { formatDateTime, pluralise } from '../lib/format'
import { useSession } from '../state/session'
import { useToday } from '../state/today'
import { useToast } from '../state/toast'
import { SiteSelect } from '../components/AddSiteSheet'
import { PhotoPicker, type AttachedPhoto } from '../components/PhotoPicker'
import { Button, EmptyState, Field, NumberInput, SectionHeading, Sheet, Spinner } from '../components/ui'

type MoveDirection = 'out' | 'in' | 'move'

/** Out of the shop, back to it, or sideways between sites. */
const MOVEMENT_ARROW: Record<MoveDirection, string> = { out: '→', in: '←', move: '⇄' }

interface Line {
  itemId: string
  qty: number
  condition?: 'ok' | 'damaged' | 'lost'
  /** in / move: the holding it comes from. */
  holderId?: string
  fromSiteId?: string | null
}

/** One holding: an item, the person it is with, and the site it is at. */
const holdingKey = (row: OutstandingRow) => `${row.itemId}|${row.userId}|${row.siteId ?? ''}`

/** Total units across a selection — the number people check the button against. */
const unitsIn = (lines: Line[]) => lines.reduce((n, line) => n + line.qty, 0)

export default function InventoryPage() {
  const { state, refresh } = useToday()
  const toast = useToast()
  const [items, setItems] = useState<InventoryItem[] | null>(null)
  const [outstanding, setOutstanding] = useState<OutstandingRow[]>([])
  const [recent, setRecent] = useState<InventoryTransaction[]>([])
  const [sheet, setSheet] = useState<MoveDirection | null>(null)
  /** null = closed; a string (possibly empty) opens the add sheet with that name prefilled. */
  const [addName, setAddName] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const load = async () => {
    const [itemsRes, outRes, txnRes] = await Promise.all([
      get<{ items: InventoryItem[] }>('/api/inventory/items'),
      get<{ outstanding: OutstandingRow[] }>('/api/inventory/outstanding?scope=all'),
      get<{ transactions: InventoryTransaction[] }>('/api/inventory/transactions?limit=20'),
    ])
    setItems(itemsRes.items)
    setOutstanding(outRes.outstanding)
    setRecent(txnRes.transactions)
  }

  useEffect(() => {
    void load().catch(() => setItems([]))
  }, [])

  const me = state?.user.id
  const mine = outstanding.filter((row) => row.userId === me)
  const others = useMemo(() => {
    const q = search.trim().toLowerCase()
    return outstanding
      .filter((row) => row.userId !== me)
      .filter((row) => !q || [row.itemName, row.userName, row.siteName, row.sku]
        .some((v) => (v ?? '').toLowerCase().includes(q)))
  }, [outstanding, me, search])

  if (!state || !items) return <div className="py-16"><Spinner /></div>

  const totalMine = mine.reduce((n, row) => n + row.qtyOut, 0)

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <p className="label">Shop and site</p>
          <h1 className="mt-0.5 text-2xl">Gear</h1>
        </div>
        <div className="text-right">
          <p className="label">Signed out to you</p>
          <p className="font-mono text-2xl font-semibold tnum">{totalMine}</p>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-2">
        <Button variant="primary" className="px-2" onClick={() => setSheet('out')}>
          Take out
        </Button>
        <Button className="px-2" onClick={() => setSheet('in')} disabled={outstanding.length === 0}>
          Return
        </Button>
        <Button className="px-2" onClick={() => setSheet('move')} disabled={outstanding.length === 0}>
          Move site
        </Button>
        <Button className="px-2" onClick={() => setAddName('')}>
          Add new gear
        </Button>
      </div>

      <section>
        <SectionHeading>Out with you</SectionHeading>
        {mine.length === 0 ? (
          <EmptyState
            title="Nothing signed out"
            body="Anything you take from the shop shows up here until it comes back."
          />
        ) : (
          <div className="space-y-2">
            {mine.map((row) => <HoldingRow key={holdingKey(row)} row={row} />)}
          </div>
        )}
      </section>

      <section>
        <SectionHeading>Out with everyone else</SectionHeading>
        {outstanding.length - mine.length > 6 && (
          <input
            className="field mb-2"
            type="search"
            placeholder="Search gear, people or sites"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        )}
        {others.length === 0 ? (
          <p className="text-sm text-graphite-2">
            {search ? 'Nothing matches that search.' : 'Nobody else has anything signed out.'}
          </p>
        ) : (
          <div className="space-y-2">
            {others.map((row) => <HoldingRow key={holdingKey(row)} row={row} showPerson />)}
          </div>
        )}
      </section>

      <section>
        <SectionHeading>Recent activity</SectionHeading>
        {recent.length === 0 ? (
          <p className="text-sm text-graphite-2">No movements yet.</p>
        ) : (
          <div className="space-y-1.5">
            {recent.map((txn) => (
              <div
                key={txn.id}
                className="flex items-baseline justify-between gap-3 border-b border-line pb-1.5 text-sm"
              >
                <span className="min-w-0 truncate">
                  <span
                    className={`font-mono text-xs font-semibold ${
                      txn.direction === 'in' ? 'text-ok' : 'text-graphite'
                    }`}
                  >
                    {MOVEMENT_ARROW[txn.direction]} {txn.qty}
                  </span>{' '}
                  {txn.itemName}
                  <span className="text-xs text-graphite-2">
                    {' '}· {txn.userId === me ? 'you' : txn.userName}
                    {txn.fromHolderId && txn.fromHolderId !== txn.userId
                      ? ` (${txn.fromHolderId === me ? 'yours' : `${txn.fromHolderName}’s`})`
                      : ''}
                    {txn.direction === 'move' && ` · ${txn.fromSiteName ?? 'somewhere'} → ${txn.siteName ?? 'site'}`}
                  </span>
                </span>
                <span className="shrink-0 font-mono text-[10px] tracking-wider text-graphite-2 uppercase">
                  {formatDateTime(txn.occurredAt, state.timezone)}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {sheet && (
        <MoveSheet
          direction={sheet}
          items={items}
          outstanding={outstanding}
          me={state.user.id}
          defaultSiteId={state.openEntry?.siteId ?? ''}
          sites={state.sites}
          onClose={() => setSheet(null)}
          onAddNew={(name) => {
            setSheet(null)
            setAddName(name)
          }}
          onDone={async (message) => {
            setSheet(null)
            toast.success(message)
            await load().catch(() => undefined)
            await refresh()
          }}
        />
      )}

      {addName !== null && (
        <AddItemSheet
          initialName={addName}
          defaultSiteId={state.openEntry?.siteId ?? ''}
          sites={state.sites}
          onClose={() => setAddName(null)}
          onDone={async (message) => {
            setAddName(null)
            toast.success(message)
            await load().catch(() => undefined)
            await refresh()
          }}
        />
      )}
    </div>
  )
}

function Thumb({ url }: { url: string | null }) {
  if (!url) return <span className="size-10 shrink-0 rounded border border-dashed border-line-strong" aria-hidden />
  return <img src={url} alt="" loading="lazy" className="size-10 shrink-0 rounded border border-line object-cover" />
}

function HoldingRow({ row, showPerson }: { row: OutstandingRow; showPerson?: boolean }) {
  return (
    <div className="card flex items-center justify-between gap-3 p-3">
      <Thumb url={row.photoThumbUrl} />
      <div className="min-w-0 flex-1">
        <p className="truncate font-bold">{row.itemName}</p>
        <p className="truncate text-xs text-graphite-2">
          {showPerson && <span className="font-semibold text-graphite">{row.userName} · </span>}
          {row.siteName ?? 'No site recorded'}
        </p>
      </div>
      <p className="shrink-0 font-mono text-lg font-semibold tnum">
        {row.qtyOut} <span className="text-xs text-graphite-2">{row.unit}</span>
      </p>
    </div>
  )
}

/**
 * Gear bought mid-day never went through the shop, so the crew can put it on
 * the books themselves: name it, say how many, and say where it ended up. Sent
 * to a site, it shows up signed out to you like anything else; sent to the
 * shop, it becomes stock.
 */
function AddItemSheet({ initialName, defaultSiteId, sites, onClose, onDone }: {
  initialName: string
  defaultSiteId: string
  sites: { id: string; name: string }[]
  onClose: () => void
  onDone: (message: string) => Promise<void>
}) {
  const toast = useToast()
  const { settings } = useSession()
  const [photos, setPhotos] = useState<AttachedPhoto[]>([])
  const [name, setName] = useState(initialName)
  const [qty, setQty] = useState<number | null>(1)
  const [unit, setUnit] = useState('ea')
  const [consumable, setConsumable] = useState(false)
  const [siteId, setSiteId] = useState(defaultSiteId)
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (!name.trim() || !qty) return
    setBusy(true)

    const payload = {
      name: name.trim(),
      qty,
      unit: unit.trim() || 'ea',
      consumable,
      siteId: siteId || null,
      notes: notes.trim() || undefined,
      occurredAt: new Date().toISOString(),
      clientId: newClientId(),
    }
    const summary = `Added ${qty} ${unit.trim() || 'ea'} ${name.trim()}`
    const [photo] = photos
    const files = photo
      ? [
          { field: 'photo_0', blob: photo.full, filename: 'gear.jpg' },
          { field: 'thumb_0', blob: photo.thumb, filename: 'gear-thumb.jpg' },
        ]
      : []

    try {
      let res: Response
      if (photo) {
        const form = new FormData()
        form.set('payload', JSON.stringify(payload))
        for (const f of files) form.set(f.field, new File([f.blob], f.filename, { type: 'image/jpeg' }))
        form.set('dims_0', `${photo.width}x${photo.height}`)
        res = await fetch('/api/inventory/items', { method: 'POST', body: form, credentials: 'same-origin' })
      } else {
        res = await fetch('/api/inventory/items', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          credentials: 'same-origin',
        })
      }
      if (!res.ok) {
        const detail = (await res.json().catch(() => null)) as { error?: string } | null
        throw new Error(detail?.error ?? 'Could not add that item')
      }
      await onDone(summary)
    } catch (err) {
      if (isOffline(err)) {
        await enqueue({
          kind: 'inventory',
          url: '/api/inventory/items',
          method: 'POST',
          payload,
          files,
          summary,
        })
        await onDone('Saved offline. It will send when you get signal.')
      } else {
        toast.error(err instanceof Error ? err.message : 'Could not add that item')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title="Add new gear"
      footer={
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          loading={busy}
          disabled={!name.trim() || !qty}
          onClick={() => void submit()}
        >
          {!name.trim() ? 'Name it first' : 'Add it to the books'}
        </Button>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-graphite-2">
          Bought something during the day? Put it on the books here so the office
          knows it exists and where it is.
        </p>
        <div>
          <span className="label mb-1.5 block">Photo</span>
          <PhotoPicker
            photos={photos}
            onChange={setPhotos}
            max={1}
            maxDimension={settings.photo.maxDimension}
            quality={settings.photo.quality}
          />
          <span className="mt-1 block text-xs text-graphite-2">
            What it looks like, so anyone can pick it out on site.
          </span>
        </div>
        <Field label="What is it" required>
          <input
            className="field"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="DeWalt 20V impact driver"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="How many" required>
            <NumberInput value={qty} onChange={setQty} min={1} max={1_000_000} />
          </Field>
          <Field label="Unit">
            <input
              className="field"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              placeholder="ea"
            />
          </Field>
        </div>
        <Field
          label="Where is it now"
          hint={siteId
            ? consumable
              ? 'It will be logged as used at this site.'
              : 'It will show as signed out to you at this site.'
            : 'It goes straight into shop stock.'}
        >
          <SiteSelect value={siteId} onChange={setSiteId} sites={sites} emptyLabel="The shop" />
        </Field>
        <label className="flex min-h-12 items-center gap-3">
          <input
            type="checkbox"
            className="size-5 accent-graphite"
            checked={consumable}
            onChange={(e) => setConsumable(e.target.checked)}
          />
          <span className="text-sm">
            <span className="font-bold">Gets used up</span>
            <span className="block text-xs text-graphite-2">
              Caulking, screws, blades — things nobody returns.
            </span>
          </span>
        </label>
        <Field label="Note" hint="Optional — receipt number, which store, who asked for it.">
          <input
            className="field"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Home Depot, receipt in the truck"
          />
        </Field>
      </div>
    </Sheet>
  )
}

function MoveSheet({
  direction, items, outstanding, me, sites, defaultSiteId, onClose, onAddNew, onDone,
}: {
  direction: MoveDirection
  items: InventoryItem[]
  outstanding: OutstandingRow[]
  me: string
  sites: { id: string; name: string }[]
  defaultSiteId: string
  onClose: () => void
  /** Take-out only: hand off to the add-new-gear sheet, keeping the search text. */
  onAddNew: (name: string) => void
  onDone: (message: string) => Promise<void>
}) {
  const toast = useToast()
  const [search, setSearch] = useState('')
  const [lines, setLines] = useState<Record<string, Line>>({})
  const [siteId, setSiteId] = useState(defaultSiteId)
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)

  // Returning and moving offer everything that is out, whoever has it — yours
  // first — one row per person and site, so "2 drills at Riverbend" and "1
  // drill at Northgate" can be handled separately. Taking out offers the shop.
  const pool = useMemo(() => {
    if (direction === 'in' || direction === 'move') {
      return [...outstanding]
        .sort((a, b) => Number(b.userId === me) - Number(a.userId === me))
        .map((row) => ({
          key: holdingKey(row),
          id: row.itemId,
          name: row.itemName,
          sku: row.sku,
          unit: row.unit,
          available: row.qtyOut,
          thumb: row.photoThumbUrl,
          holderId: row.userId,
          holderName: row.userId === me ? 'you' : row.userName,
          siteId: row.siteId,
          siteName: row.siteName,
        }))
    }
    return items.map((item) => ({
      key: item.id,
      id: item.id,
      name: item.name,
      sku: item.sku,
      unit: item.unit,
      available: item.qtyOnHand,
      thumb: item.photoThumbUrl,
      holderId: null as string | null,
      holderName: null as string | null,
      siteId: null as string | null,
      siteName: null as string | null,
    }))
  }, [direction, items, outstanding, me])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return pool
    return pool.filter((item) => [item.name, item.sku, item.holderName, item.siteName]
      .some((v) => (v ?? '').toLowerCase().includes(q)))
  }, [pool, search])

  const selected = Object.values(lines).filter((line) => line.qty > 0)
  // A move has nowhere to go without a site; out and in can both be shop-only.
  const needsDestination = direction === 'move' && !siteId

  const setQty = (entry: (typeof pool)[number], qty: number) => {
    const clamped = Math.max(0, Math.min(entry.available, qty))
    setLines((current) => ({
      ...current,
      [entry.key]: {
        ...current[entry.key],
        itemId: entry.id,
        qty: clamped,
        condition: current[entry.key]?.condition ?? 'ok',
        holderId: entry.holderId ?? undefined,
        fromSiteId: entry.siteId,
      },
    }))
  }
  const units = unitsIn(selected)

  const submit = async () => {
    if (!selected.length) return
    setBusy(true)

    const payload = {
      direction,
      siteId: siteId || null,
      notes: notes.trim() || undefined,
      occurredAt: new Date().toISOString(),
      clientId: newClientId(),
      // Each line names the holding it comes from, so the server takes it
      // from the right person at the right site.
      lines: selected.map((line) => ({
        itemId: line.itemId,
        qty: line.qty,
        condition: direction === 'in' ? line.condition ?? 'ok' : undefined,
        holderId: direction === 'out' ? undefined : line.holderId,
        fromSiteId: direction === 'out' ? undefined : line.fromSiteId ?? null,
      })),
    }

    const summary = `${
      direction === 'out' ? 'Took out' : direction === 'move' ? 'Moved' : 'Returned'
    } ${pluralise(units, 'item')}`

    try {
      const res = await fetch('/api/inventory/move', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'same-origin',
      })
      if (!res.ok) {
        const detail = (await res.json().catch(() => null)) as { error?: string } | null
        throw new Error(detail?.error ?? 'Could not record that move')
      }
      await onDone(summary)
    } catch (err) {
      if (isOffline(err)) {
        await enqueue({
          kind: 'inventory',
          url: '/api/inventory/move',
          method: 'POST',
          payload,
          files: [],
          summary,
        })
        await onDone('Saved offline. It will send when you get signal.')
      } else {
        toast.error(err instanceof Error ? err.message : 'Could not record that move')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title={
        direction === 'out' ? 'Take gear out of the shop'
          : direction === 'move' ? 'Move gear to another site'
            : 'Return gear'
      }
      footer={
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          loading={busy}
          disabled={selected.length === 0 || needsDestination}
          onClick={() => void submit()}
        >
          {selected.length === 0
            ? 'Pick something first'
            : needsDestination
              ? 'Choose a site to move to'
              // Count units, not rows: taking 5 of one thing is "5 items".
              : `${direction === 'out' ? 'Sign out' : direction === 'move' ? 'Move' : 'Return'} ${pluralise(units, 'item')}`}
        </Button>
      }
    >
      <div className="space-y-4">
        {direction === 'in' ? (
          <p className="text-sm text-graphite-2">
            Everything below goes back to the shop, recorded against the site it is
            currently at.
          </p>
        ) : (
          <Field
            label="Going to"
            hint={direction === 'move'
              ? 'It shows as with you at the new site — including gear someone else signed out.'
              : undefined}
          >
            <SiteSelect
              value={siteId}
              onChange={setSiteId}
              sites={sites}
              emptyLabel={direction === 'move' ? 'Choose a site…' : 'No site'}
            />
          </Field>
        )}

        <input
          className="field"
          type="search"
          placeholder="Search gear"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="space-y-1.5">
          {filtered.map((item) => {
            const line = lines[item.key]
            const qty = line?.qty ?? 0
            return (
              <div key={item.key} className="border-b border-line pb-1.5">
                <div className="flex items-center gap-2">
                  <Thumb url={item.thumb} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold">{item.name}</p>
                    <p className="font-mono text-[10px] tracking-wider text-graphite-2 uppercase">
                      {item.sku ? `${item.sku} · ` : ''}
                      {item.available} {item.unit} {direction === 'out' ? 'on hand' : `with ${item.holderName}`}
                      {direction !== 'out' && item.siteName ? ` at ${item.siteName}` : ''}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      className="btn btn-secondary size-10 min-h-10 p-0 text-lg"
                      onClick={() => setQty(item, qty - 1)}
                      disabled={qty === 0}
                      aria-label={`One fewer ${item.name}`}
                    >
                      –
                    </button>
                    <span className="w-8 text-center font-mono font-semibold tnum">{qty}</span>
                    <button
                      type="button"
                      className="btn btn-secondary size-10 min-h-10 p-0 text-lg"
                      onClick={() => setQty(item, qty + 1)}
                      disabled={qty >= item.available}
                      aria-label={`One more ${item.name}`}
                    >
                      +
                    </button>
                  </div>
                </div>

                {direction === 'in' && qty > 0 && (
                  <div className="mt-1.5 flex gap-1">
                    {(['ok', 'damaged', 'lost'] as const).map((condition) => (
                      <button
                        key={condition}
                        type="button"
                        onClick={() =>
                          setLines((current) => ({
                            ...current,
                            [item.key]: { ...current[item.key], itemId: item.id, qty, condition },
                          }))}
                        className={`chip ${
                          (line?.condition ?? 'ok') === condition
                            ? 'bg-graphite text-concrete'
                            : 'text-graphite-2'
                        }`}
                        style={
                          (line?.condition ?? 'ok') === condition
                            ? undefined
                            : { boxShadow: 'inset 0 0 0 1px var(--wm-line-strong)' }
                        }
                      >
                        {condition}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
          {filtered.length === 0 && (
            <div className="space-y-3 py-6 text-center">
              <p className="text-sm text-graphite-2">
                {direction === 'out'
                  ? 'No gear matches that search.'
                  : 'Nothing out matches that.'}
              </p>
              {direction === 'out' && (
                <Button onClick={() => onAddNew(search.trim())}>
                  {search.trim() ? `Add “${search.trim()}” as new gear` : 'Add new gear'}
                </Button>
              )}
            </div>
          )}
        </div>

        <Field label="Note" hint="Optional — anything the shop should know.">
          <input
            className="field"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Generator needs a service"
          />
        </Field>
      </div>
    </Sheet>
  )
}
